import {
  type CodeResponse,
  type CoverageGroup,
  type CoverageResponse,
  type GroupType,
  getCoverage,
  getLocationCoverage,
  type LocationCoverageResponse,
} from '@lms/api-client/backend';
import { toLocalDateString } from '@lms/api-client/dates';
import type { ShareRow } from '@lms/charts';
import useSWR from 'swr';

/** The period group figures are counted in (same as the stats toolbar). */
export interface CoveragePeriod {
  period: 'since' | 'year' | 'month' | 'day';
  fromDate?: Date;
}

function periodParams({ period, fromDate }: CoveragePeriod) {
  return {
    period,
    fromDate: fromDate ? toLocalDateString(fromDate) : undefined,
  };
}

/**
 * Completions per group × curriculum in a period; with `codes`, only students
 * who signed up with one of them count.
 */
export function useCoverage(
  locationId: number | undefined,
  locale: string,
  scope: CoveragePeriod,
  codes: number[] = [],
) {
  const params = periodParams(scope);
  return useSWR(
    locationId !== undefined
      ? [
          'stats/coverage',
          locationId,
          locale,
          params.period,
          params.fromDate,
          codes,
        ]
      : null,
    () =>
      getCoverage([locationId as number], { ...params, locale, code: codes }),
    { keepPreviousData: true },
  );
}

/**
 * Per location: groups without completions in a period (portfolios). With
 * `curricula`, only completions in those and only groups expected to take
 * one of them count.
 */
export function useLocationCoverage(
  locations: number[],
  scope: CoveragePeriod,
  curricula: number[] = [],
) {
  const params = periodParams(scope);
  return useSWR(
    locations.length > 0
      ? [
          'stats/coverage/locations',
          params.period,
          params.fromDate,
          curricula,
          ...locations,
        ]
      : null,
    () => getLocationCoverage(locations, { ...params, curriculum: curricula }),
    { keepPreviousData: true },
  );
}

/** Totals across locations for one group type. */
export function totalWithCompletions(
  coverage: LocationCoverageResponse,
  type: GroupType,
): { groups: number; withCompletions: number } {
  return coverage.locations
    .filter((l) => l.groupType === type)
    .reduce(
      (acc, l) => ({
        groups: acc.groups + l.groups,
        withCompletions: acc.withCompletions + l.groups - l.withoutCompletions,
      }),
      { groups: 0, withCompletions: 0 },
    );
}

/**
 * One row per location, split by group type: groups with completions out of
 * all groups. Every row has a part per type in `types` (a zero total when the
 * location has none) so parts line up across rows. Lowest combined share
 * first, so the locations furthest from 100 % lead.
 */
export function locationSharesByType(
  coverage: LocationCoverageResponse,
  types: readonly { type: GroupType; label: string }[],
): ShareRow[] {
  const byLocation = new Map<
    number,
    { name: string; entries: typeof coverage.locations }
  >();
  for (const l of coverage.locations) {
    const cur = byLocation.get(l.locationId) ?? {
      name: l.locationName,
      entries: [],
    };
    cur.entries.push(l);
    byLocation.set(l.locationId, cur);
  }
  return [...byLocation.entries()]
    .map(([id, { name, entries }]) => {
      const parts = types.map(({ type, label }) => {
        const e = entries.find((l) => l.groupType === type);
        return {
          id: type,
          label,
          value: e ? e.groups - e.withoutCompletions : 0,
          total: e?.groups ?? 0,
        };
      });
      return {
        id,
        name,
        value: parts.reduce((sum, p) => sum + p.value, 0),
        total: parts.reduce((sum, p) => sum + p.total, 0),
        parts,
      };
    })
    .filter((r) => r.total > 0)
    .sort(
      (a, b) =>
        a.value / a.total - b.value / b.total || a.name.localeCompare(b.name),
    );
}

/** Completions in the period per group, keyed `${groupId}:${curriculumId}`. */
function cellIndex(coverage: CoverageResponse) {
  return new Map(
    coverage.cells.map((c) => [`${c.groupId}:${c.curriculumId}`, c]),
  );
}

export function groupsOfType(coverage: CoverageResponse, type: GroupType) {
  return coverage.groups.filter((g) => g.groupType === type);
}

/** Group ids with at least one completion in the period (any curriculum). */
function activeGroupIds(coverage: CoverageResponse): Set<number> {
  return new Set(
    coverage.cells.filter((c) => c.completed > 0).map((c) => c.groupId),
  );
}

/** Number of groups of a type with at least one completion in the period. */
export function countWithCompletions(
  coverage: CoverageResponse,
  type: GroupType,
): number {
  return (
    groupsOfType(coverage, type).length -
    countWithoutCompletions(coverage, type)
  );
}

/** Number of groups of a type with no completions in the period. */
export function countWithoutCompletions(
  coverage: CoverageResponse,
  type: GroupType,
): number {
  const active = activeGroupIds(coverage);
  return groupsOfType(coverage, type).filter((g) => !active.has(g.id)).length;
}

/** Whether a group is expected to take a curriculum. */
export type Expected = (group: CoverageGroup, curriculumId: number) => boolean;

/**
 * Expected group × curriculum pairs from the location's codes: a standard
 * code links its own group; a tenant-select code links every tenant group,
 * a supplier-select or custom-supplier code every supplier group.
 */
export function expectedFromCodes(
  codes: readonly Pick<CodeResponse, 'curriculumId' | 'groupId' | 'codeType'>[],
): Expected {
  const byGroup = new Set<string>();
  const byType = new Set<string>();
  for (const c of codes) {
    if (c.codeType === 'standard') {
      if (c.groupId !== null) byGroup.add(`${c.groupId}:${c.curriculumId}`);
    } else {
      const type: GroupType =
        c.codeType === 'tenant_select' ? 'tenant' : 'supplier';
      byType.add(`${type}:${c.curriculumId}`);
    }
  }
  return (group, curriculumId) =>
    byGroup.has(`${group.id}:${curriculumId}`) ||
    byType.has(`${group.groupType}:${curriculumId}`);
}

/**
 * Per curriculum: how many groups of a type have completions in the period,
 * out of the groups in scope for it (expected, or with completions anyway).
 */
export function curriculumShares(
  coverage: CoverageResponse,
  type: GroupType,
  expected: Expected,
): ShareRow[] {
  const cells = cellIndex(coverage);
  const groups = groupsOfType(coverage, type);
  return coverage.curricula.map((cur) => {
    let value = 0;
    let total = 0;
    for (const g of groups) {
      const done = (cells.get(`${g.id}:${cur.id}`)?.completed ?? 0) > 0;
      if (done) value += 1;
      if (done || expected(g, cur.id)) total += 1;
    }
    return { id: cur.id, name: cur.name, value, total };
  });
}

export interface GroupCompletions {
  group: CoverageGroup;
  /** Latest completion ever: in the curriculum when filtered, else any. */
  lastCompleted: string | null;
  /**
   * Per column curriculum: the latest completion in the period (null: none),
   * and whether the group is expected to take it.
   */
  cells: { last: string | null; expected: boolean }[];
  /** Whether an expected curriculum has no completion in the period. */
  missing: boolean;
}

/**
 * Every group (tenants and suppliers) with its latest completion in the
 * period per curriculum: one column per curriculum, or only the selected one.
 * With a curriculum, only groups in scope for it (expected, or with
 * completions in it anyway). Groups missing something come first: those that
 * never completed, then the longest since their last completion; then the
 * rest by name.
 */
export function groupCompletions(
  coverage: CoverageResponse,
  curriculumId: number | null,
  expected: Expected,
  locale?: string,
): { curricula: CoverageResponse['curricula']; rows: GroupCompletions[] } {
  const cells = cellIndex(coverage);
  const curricula =
    curriculumId === null
      ? coverage.curricula
      : coverage.curricula.filter((c) => c.id === curriculumId);

  const rows = coverage.groups.flatMap((group) => {
    const row = curricula.map((c) => ({
      last: cells.get(`${group.id}:${c.id}`)?.lastInPeriod ?? null,
      expected: expected(group, c.id),
    }));
    if (curriculumId !== null && !row[0]?.last && !row[0]?.expected) {
      return [];
    }
    return [
      {
        group,
        lastCompleted:
          curriculumId === null
            ? group.lastCompleted
            : (cells.get(`${group.id}:${curriculumId}`)?.lastCompleted ?? null),
        cells: row,
        missing: row.some((c) => c.expected && c.last === null),
      },
    ];
  });

  rows.sort((a, b) => {
    if (a.missing !== b.missing) return a.missing ? -1 : 1;
    if (!a.missing || a.lastCompleted === b.lastCompleted) {
      return a.group.name.localeCompare(b.group.name, locale);
    }
    if (a.lastCompleted === null) return -1;
    if (b.lastCompleted === null) return 1;
    return a.lastCompleted.localeCompare(b.lastCompleted);
  });
  return { curricula, rows };
}
